import type { IconName } from 'Components/vm-icon/vm-icon';
import 'Components/vm-icon/vm-icon';
import { html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import Styles from './vm-toast.scss';

const DEFAULT_DURATION = 5;

export interface VmToastShowDetail {
    id?: string;
    message: string;
    variant?: 'confirmation' | 'error';
    duration?: number;
    icon?: IconName;
    link?: { text: string; href: string };
}

/**
 * A toast notification component.
 *
 * Toasts are created and managed by `<vm-toast-container>`, which listens for
 * `vm-toast-show` events on `document` and calls the `show()` method.
 *
 * @fires vm-toast-close - Fired after the hide transition ends, signalling the container to remove the element.
 *
 * @example
 * ```ts
 * // Trigger a toast from anywhere:
 * document.dispatchEvent(new CustomEvent('vm-toast-show', {
 *     detail: { message: 'Saved!', variant: 'confirmation', icon: 'award-03' },
 * }));
 *
 * // With a link and custom duration:
 * document.dispatchEvent(new CustomEvent('vm-toast-show', {
 *     detail: {
 *         message: 'Item deleted.',
 *         variant: 'error',
 *         icon: 'link-01',
 *         duration: 10,
 *         link: { text: 'Undo', href: '/undo' },
 *     },
 * }));
 *
 * // With an id to prevent duplicates:
 * document.dispatchEvent(new CustomEvent('vm-toast-show', {
 *     detail: { id: 'save-toast', message: 'Saved!' },
 * }));
 * ```
 */
@customElement('vm-toast')
export class VmToast extends LitElement {
    static override styles = Styles;

    /** Visual variant of the toast. Reflects to the host attribute for CSS styling. */
    @property({ reflect: true })
    variant: 'confirmation' | 'error' = 'confirmation';

    /** Whether the toast is currently visible. Controls the CSS transition. */
    @property({ type: Boolean, reflect: true })
    visible: boolean = false;

    /** Auto-dismiss delay in seconds. Set to 0 to disable auto-dismiss. */
    @property({ type: Number })
    duration: number = DEFAULT_DURATION;

    @state()
    message: string = '';

    @state()
    icon: IconName | '' = '';

    @state()
    link: { text: string; href: string } | null = null;

    /** Dedupe key — the container uses this to avoid showing the same toast twice. */
    toastId: string = '';

    /** True once `close()` has been called — used by the container for FIFO accounting. */
    isClosing: boolean = false;

    private _timer: ReturnType<typeof setTimeout> | null = null;
    private _closed: boolean = false;

    override connectedCallback() {
        super.connectedCallback();
        this.classList.add('vm-toast');
        this.dataset.testid = 'vm-toast';
        this.setAttribute('role', 'alert');
        this.setAttribute('aria-live', 'polite');
        this.addEventListener('transitionend', this._onTransitionEnd as EventListener);
    }

    override disconnectedCallback() {
        super.disconnectedCallback();
        this._clearTimer();
        this.removeEventListener('transitionend', this._onTransitionEnd as EventListener);
    }

    override updated(changed: Map<string, unknown>) {
        if (changed.has('visible')) {
            if (this.visible) {
                this.startTimer();
            } else if (changed.get('visible') === true) {
                this._clearTimer();
            }
        }
    }

    /** Populate the toast and make it visible. Called by `<vm-toast-container>`. */
    show(detail: VmToastShowDetail) {
        this.toastId = detail.id ?? '';
        this.message = detail.message;
        this.variant = detail.variant ?? 'confirmation';
        this.duration = detail.duration ?? DEFAULT_DURATION;
        this.icon = detail.icon ?? '';
        this.link = detail.link ?? null;
        this.visible = true;
    }

    /**
     * (Re)start the auto-dismiss countdown.
     * Public so the container can restart the timer on an already-visible toast during dedup
     * without re-triggering the full show() flow (which wouldn't fire updated() since `visible`
     * hasn't changed).
     */
    startTimer() {
        this._clearTimer();
        if (this.duration > 0) {
            this._timer = setTimeout(() => this.close(), this.duration * 1000);
        }
    }

    private _clearTimer() {
        if (this._timer) {
            clearTimeout(this._timer);
            this._timer = null;
        }
    }

    close() {
        if (this._closed) return;
        this._closed = true;
        this.isClosing = true;
        this.visible = false;
        // Safety net: if transitionend doesn't fire (e.g., prefers-reduced-motion), still dispatch close.
        setTimeout(() => {
            if (this.isConnected) {
                this.dispatchEvent(
                    new CustomEvent('vm-toast-close', {
                        bubbles: true,
                        composed: true,
                    }),
                );
            }
        }, 400);
    }

    private _getSafeHref(href: string): string {
        try {
            const url = new URL(href, window.location.origin);
            return ['http:', 'https:'].includes(url.protocol) ? href : '#';
        } catch {
            return href.startsWith('/') ? href : '#';
        }
    }

    /** After the CSS transition completes, fire `vm-toast-close` so the container can remove this element. */
    private _onTransitionEnd = (event: TransitionEvent) => {
        if (event.propertyName !== 'transform') return;
        if (!this.visible) {
            this.dispatchEvent(
                new CustomEvent('vm-toast-close', {
                    bubbles: true,
                    composed: true,
                }),
            );
        }
    };

    override render() {
        return html`
            ${this.icon ? html`<vm-icon name="${this.icon}"></vm-icon>` : nothing}
            <div class="vm-toast__text" data-testid="vm-toast-text">${this.message}</div>
            ${this.link
                ? html`<a class="vm-toast__link" href="${this._getSafeHref(this.link.href)}">${this.link.text}</a>`
                : nothing}
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-toast': VmToast;
    }
}
